<template>
  <div style="display: none;">
    <!-- 不可见的身份验证监控组件 -->
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getToken, parseUrlTokenAndSave, isAuthValid, isInResetPasswordMode } from '@/utils/auth';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const authStore = useAuthStore();
const checkInterval = ref(null);
const lastActiveTime = ref(Date.now());
const wasInactive = ref(false);

// 检查登录状态
const checkAuth = () => {
  // 🔧 定义公共页面白名单（与路由守卫保持一致）
  const publicPages = ['/login', '/register', '/reset-password', '/forgot-password', '/activate', '/reset-pwd']
  
  // 🔧 获取当前路径和目标路径（处理路由尚未完全就绪的情况）
  const currentPath = router.currentRoute.value.path
  
  // 🔧 如果用户正在访问的URL包含公共页面路径，跳过认证检查
  // 这样可以处理直接访问 /register?invite=xxx 的情况
  const currentUrl = window.location.pathname
  const isAccessingPublicPage = publicPages.some(page => currentUrl.startsWith(page))
  
  if (isAccessingPublicPage) {
    console.log(`Auth Monitor: 正在访问公共页面 ${currentUrl}，跳过认证检查`)
    return
  }
  
  // 如果当前已经在公共页面，也跳过认证检查
  if (publicPages.includes(currentPath)) {
    console.log(`Auth Monitor: 当前在公共页面 ${currentPath}，跳过认证检查`)
    return
  }
  
  // 检查是否处于重置密码模式
  if (isInResetPasswordMode()) {
    console.log('Auth Monitor: 检测到重置密码模式，跳过认证检查')
    return
  }
  
  const token = localStorage.getItem('access_token') || localStorage.getItem('token')
  
  // 如果没有token，且不在公共页面，则跳转到登录页
  // 🔧 双重检查：既检查 Vue Router 的当前路径，也检查浏览器实际URL
  const isOnPublicPage = publicPages.includes(router.currentRoute.value.path) || 
                         publicPages.some(page => window.location.pathname.startsWith(page))
  
  if (!token && !isOnPublicPage) {
    
    // 防抖：避免频繁跳转
    const lastRedirectTime = window._lastAuthRedirectTime || 0
    const now = Date.now()
    if (now - lastRedirectTime < 5000) {
      console.log('Auth Monitor: 跳过重复跳转（5秒内已跳转过）')
      return
    }
    
    window._lastAuthRedirectTime = now
    console.log('Auth Monitor: 检测到未登录状态，跳转到登录页');
    
    // 记录当前状态，用于排查
    const authState = {
      path: currentPath,
      time: new Date().toISOString(),
      tokenExists: !!token,
      tokenLength: token ? token.length : 0,
      visibilityState: document.visibilityState,
      timeSinceLastActive: Date.now() - lastActiveTime.value,
      localStorage: Object.keys(localStorage).filter(key => 
        key.includes('token') || key.includes('user')
      )
    };
    
    console.log('Auth Monitor: 认证状态详情:', authState);
    
    // 清理本地存储
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_info');
    
    // 跳转到登录页
    router.push('/login');
    return;
  }
  
  // 如果有token，验证其有效性
  if (token) {
    // 这里可以添加token有效性检查
    // 比如检查token是否过期等
    console.log('Auth Monitor: 存在token，长度:', token.length);
  }
}

// 处理页面可见性变化
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    console.log('AuthMonitor: 页面变为可见状态');
    
    // ✅ 检查是否在公共页面，如果是则跳过token验证
    const publicPages = ['/login', '/register', '/reset-password', '/forgot-password', '/activate', '/reset-pwd']
    const currentUrl = window.location.pathname
    const isAccessingPublicPage = publicPages.some(page => currentUrl.startsWith(page))
    
    if (isAccessingPublicPage) {
      console.log('AuthMonitor: 在公共页面，跳过token验证')
      return
    }
    
    // 记录可见性变化时间
    localStorage.setItem('last_visibility_change', Date.now().toString());
    
    // 计算不活跃时间
    const inactiveTime = Date.now() - lastActiveTime.value;
    console.log(`AuthMonitor: 页面不活跃时间: ${inactiveTime/1000}秒`);
    
    // 如果不活跃时间超过30秒，标记为需要恢复
    if (inactiveTime > 30000) {
      wasInactive.value = true;
      
      // 主动尝试恢复认证状态
      try {
        // 尝试刷新token状态
        authStore.validateToken();
        console.log('AuthMonitor: 页面重新激活，已尝试恢复认证状态');
      } catch (error) {
        console.error('AuthMonitor: 恢复认证状态失败', error);
      }
    }
    
    // 立即检查一次认证状态
    checkAuth();
  } else {
    console.log('AuthMonitor: 页面变为隐藏状态');
  }
};

onMounted(() => {
  // 记录初始活跃时间
  lastActiveTime.value = Date.now();
  
  // 页面加载后立即检查一次
  checkAuth();
  
  // 每分钟检查一次登录状态
  checkInterval.value = setInterval(checkAuth, 60000);
  
  // 监听浏览器存储变化，检测token是否被清除
  window.addEventListener('storage', (e) => {
    if (e.key === 'access_token' && !e.newValue) {
      checkAuth();
    }
  });
  
  // 监听URL变化，检查是否有token参数
  window.addEventListener('popstate', checkAuth);
  
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // 监听用户活动，更新最后活跃时间
  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(eventType => {
    document.addEventListener(eventType, () => {
      lastActiveTime.value = Date.now();
    }, { passive: true });
  });
});

onBeforeUnmount(() => {
  if (checkInterval.value) {
    clearInterval(checkInterval.value);
  }
  window.removeEventListener('storage', () => {});
  window.removeEventListener('popstate', checkAuth);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  
  // 移除用户活动监听
  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(eventType => {
    document.removeEventListener(eventType, () => {});
  });
});
</script>